import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { submitSetup } from '../../api/auth';
import { ApiError } from '../../api/client';
import { NewPasswordFields, newPasswordProblem } from '../../components/account/NewPasswordFields';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function setupErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Не удалось связаться с сервером. Попробуйте ещё раз.';
  if (err.code === 'invalid_setup_token') return 'Неверный токен настройки. Проверьте значение SETUP_TOKEN на сервере.';
  if (err.code === 'invalid_body') return 'Проверьте поля формы: название, имя и email обязательны.';
  if (err.status === 429) return 'Слишком много попыток. Подождите минуту и попробуйте ещё раз.';
  // 409 (уже настроено), 503 (нет SETUP_TOKEN), weak_password — сервер отвечает понятным текстом.
  return err.message;
}

/**
 * First-run screen (`/#/setup?token=…`), shown automatically while the
 * instance has no users. Creates the company workspace and its owner; the
 * token comes from the server's SETUP_TOKEN and is usually carried by the link.
 */
export function SetupPage({ onDone }: { onDone?: () => void }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get('token') ?? '');
  const [workspaceName, setWorkspaceName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState({ password: '', repeat: '' });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem = newPasswordProblem(pw, email);
  const complete = token.trim() && workspaceName.trim() && name.trim() && email.trim() && !problem;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!complete) return;
    setPending(true);
    setError(null);
    try {
      await submitSetup({
        setup_token: token.trim(),
        workspace_name: workspaceName.trim(),
        name: name.trim(),
        email: email.trim(),
        password: pw.password,
      });
      onDone?.();
      void navigate('/login', { replace: true });
    } catch (err) {
      setError(setupErrorText(err));
      if (err instanceof ApiError && err.code === 'already_configured') onDone?.();
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-muted/40 py-8">
      <form onSubmit={(e) => void handleSubmit(e)} className="w-96 rounded border border-border bg-card p-6 shadow-sm">
        <h1 className="mb-1 text-lg font-semibold">Первичная настройка</h1>
        <p className="mb-4 text-sm text-muted-fg">
          В системе ещё нет ни одной учётной записи. Создайте рабочее пространство компании и владельца — он сможет
          добавить остальных сотрудников.
        </p>
        <label className="mb-3 block text-sm" htmlFor="setup-token">
          Токен настройки
          <Input
            id="setup-token"
            className="mt-1"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
          />
          <span className="mt-1 block text-xs text-muted-fg">Значение SETUP_TOKEN с сервера. Его знает тот, кто разворачивал систему.</span>
        </label>
        <label className="mb-3 block text-sm" htmlFor="setup-workspace">
          Название компании
          <Input id="setup-workspace" className="mt-1" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} required />
        </label>
        <label className="mb-3 block text-sm" htmlFor="setup-name">
          Ваше имя
          <Input id="setup-name" className="mt-1" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className="mb-3 block text-sm" htmlFor="setup-email">
          Email
          <Input
            id="setup-email"
            className="mt-1"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <NewPasswordFields idPrefix="setup" value={pw} onChange={setPw} email={email} />
        {error && (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={pending || !complete}>
          {pending ? 'Создаём…' : 'Создать и перейти ко входу'}
        </Button>
      </form>
    </div>
  );
}
